import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { deleteBrand, fetchBrandsList } from "../../shared/catalogMastersApi";
import { IconEye, IconPlus } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

export function BrandsList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const brandById = useMemo(() => {
    const m = new Map();
    for (const b of items) m.set(b.id, b);
    return m;
  }, [items]);

  const rows = useMemo(() => {
    return items.map((x) => {
      const parent = x.parentId != null ? brandById.get(x.parentId) : null;
      return {
        id: x.id,
        brand: x,
        parentName: parent?.name ?? "—"
      };
    });
  }, [items, brandById]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fetchBrandsList();
    if (!result.ok) {
      setError(result.error ?? "Failed to load data");
      setItems([]);
    } else {
      setItems(result.items);
    }
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this brand?"))) return;
      const res = await deleteBrand(id);
      if (!res.ok) setError(res.error ?? "Failed to delete brand");
      else await load();
    },
    [load]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.85,
        minWidth: 110,
        valueGetter: (_v, row) => row.brand.name ?? "-"
      },
      {
        field: "parentName",
        headerName: "Parent",
        flex: 1,
        minWidth: 120,
        valueGetter: (_v, row) => row.parentName
      },
      {
        field: "slug",
        headerName: "Slug",
        flex: 0.9,
        minWidth: 100,
        valueGetter: (_v, row) => row.brand.slug ?? "-"
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const x = params.row.brand;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="View" onClick={() => setViewRow(params.row)}>
                <IconEye />
              </button>
              <button className="icon-button" type="button" title="Edit" onClick={() => navigate(`/brands?edit=${x.id}`)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(x.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [navigate, handleDeleteClick]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Brands</h2>
          <p>{loading ? "Loading brands..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <Link to="/brands" className="icon-button icon-button--toolbar" title="Add brand" aria-label="Add brand">
            <IconPlus />
          </Link>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={{
              border: "none",
              fontFamily: "inherit",
              fontSize: "0.9rem",
              height: "100%",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f2fcf5",
                borderBottom: "1px solid #e8ecea",
                fontWeight: 600
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e8ecea",
                backgroundColor: "#fbfffc"
              },
              "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
                backgroundColor: "#fcfffd"
              },
              "& .MuiDataGrid-cell": {
                borderBottomColor: "#e8ecea"
              }
            }}
          />
        </div>
      </div>
      <RecordViewDrawer
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        label="Brand details"
        title={viewRow?.brand?.name ?? "—"}
        subtitle={viewRow?.brand?.slug}
        ariaLabel="Brand details"
        images={
          viewRow
            ? [
                viewRow.brand.logo ? { label: "Logo", src: viewRow.brand.logo, linkText: "Open logo" } : null,
                viewRow.brand.banner ? { label: "Banner", src: viewRow.brand.banner, linkText: "Open banner" } : null
              ].filter(Boolean)
            : []
        }
        sections={
          viewRow
            ? [
                {
                  title: "Brand details",
                  columns: 2,
                  fields: [
                    ["Name", viewRow.brand.name],
                    ["Parent", viewRow.parentName],
                    ["Slug", viewRow.brand.slug],
                    ["Description", viewRow.brand.description, true]
                  ]
                },
                {
                  title: "SEO",
                  fields: [
                    ["Meta Title", viewRow.brand.metaTitile],
                    ["Meta Description", viewRow.brand.metaDescription, true]
                  ]
                }
              ]
            : []
        }
      />
    </div>
  );
}
